import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Badge } from "@/components/common";
import { Landmark, MapPin, Building2 } from "lucide-react";

export default function Representatives() {
  const { t } = useTranslation();
  const [reps, setReps] = useState([]);
  const [parties, setParties] = useState([]);
  const [debates, setDebates] = useState([]);

  useEffect(() => {
    api.get("/representatives").then((r) => setReps(r.data));
    api.get("/parties").then((r) => setParties(r.data));
    api.get("/debates").then((r) => setDebates(r.data.filter((d) => d.stage === "approved")));
  }, []);

  const partyName = (pid) => parties.find((p) => p.id === pid)?.name || t("Independent");

  return (
    <div className="space-y-8" data-testid="representatives-page">
      <div className="border-b-2 border-foreground pb-3"><p className="overline mb-1">{t("Accountability")}</p><h1 className="font-display text-3xl font-extrabold tracking-tight">{t("Representatives & Parties")}</h1></div>

      <section>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3 flex items-center gap-2"><Building2 size={18} /> {t("Coalition parties")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {parties.map((p) => <Link key={p.id} to={`/party/${p.id}`} data-testid="rep-party" className="border border-border bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-all block"><h3 className="font-display font-bold tracking-tight">{p.name}</h3><p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p></Link>)}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3 flex items-center gap-2"><Landmark size={18} /> {t("Representatives")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reps.map((r) => (
            <div key={r.id} className="border border-border bg-white p-5" data-testid="rep-card">
              <h3 className="font-display font-bold tracking-tight">{r.name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={13} /> {r.location?.city}, {r.location?.country}</p>
              <Badge tone="purple">{partyName(r.party_id)}</Badge>
            </div>
          ))}
          {reps.length === 0 && <p className="text-sm text-muted-foreground">{t("No verified representatives yet.")}</p>}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">{t("Approved community proposals")}</h2>
        <div className="space-y-3">
          {debates.map((d) => (
            <Link key={d.id} to={`/debates/${d.id}`} className="border border-border bg-white p-5 block hover:shadow-md transition-all" data-testid="approved-proposal">
              <div className="flex items-center gap-2 mb-1"><Badge tone="purple">{t(d.lifecycle_status.replace(/_/g, " "))}</Badge><Badge>{t(d.policy_area)}</Badge></div>
              <h3 className="font-display font-bold tracking-tight">{d.title}</h3>
              <p className="text-xs text-muted-foreground mt-1">{t("Actions:")} {d.party_actions?.length || 0}</p>
            </Link>
          ))}
          {debates.length === 0 && <p className="text-sm text-muted-foreground">{t("No approved proposals yet.")}</p>}
        </div>
      </section>
    </div>
  );
}
